import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Briefcase, Calendar, Clock, DollarSign, MapPin, Plus, X, User, Phone, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import { REGIONS, LANGUAGES, SKILLS, INDUSTRIES, getMainCategories, getSubCategories, getSubSubCategories, getTrainingTypes } from '../data/globalData';

export default function PostJob() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('');
  
  // 3-tier training selection
  const [mainCategory, setMainCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [subSubCategory, setSubSubCategory] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    skills_required: [],
    qualifications_required: [],
    training_type: '',
    industry: '',
    location: '',
    venue_address: '',
    contact_person: '',
    contact_phone: '',
    training_mode: 'on-site',
    training_time: '',
    num_trainers: 1,
    trainer_roles: [],
    rate_per_hour: 0,
    start_date: '',
    end_date: '',
    duration_hours: 8,
    budget: 0,
    budget_currency: 'INR'
  });
  const [newQualification, setNewQualification] = useState('');
  const [newRole, setNewRole] = useState('');

  // Get cities for selected country
  const getCitiesForCountry = (country) => {
    if (!country) return [];
    for (const [region, countries] of Object.entries(REGIONS)) {
      if (countries[country]) {
        return countries[country];
      }
    }
    return [];
  };

  // Get all countries grouped by region
  const getCountriesByRegion = () => {
    const result = {};
    Object.entries(REGIONS).forEach(([region, countries]) => {
      result[region] = Object.keys(countries);
    });
    return result;
  };

  const toggleSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills_required: prev.skills_required.includes(skill)
        ? prev.skills_required.filter(s => s !== skill)
        : [...prev.skills_required, skill]
    }));
  };

  const addQualification = () => {
    if (newQualification.trim()) {
      setFormData(prev => ({
        ...prev,
        qualifications_required: [...prev.qualifications_required, newQualification.trim()]
      }));
      setNewQualification('');
    }
  };

  const removeQualification = (qual) => {
    setFormData(prev => ({
      ...prev,
      qualifications_required: prev.qualifications_required.filter(q => q !== qual)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || formData.skills_required.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.start_date || !formData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }
    if (!formData.training_type || !formData.location) {
      toast.error('Please select training type and location');
      return;
    }
    if (!formData.venue_address.trim()) {
      toast.error('Venue address is mandatory');
      return;
    }
    if (!formData.contact_person.trim()) {
      toast.error('Contact person name is mandatory');
      return;
    }
    if (!formData.contact_phone.trim()) {
      toast.error('Contact phone number is mandatory');
      return;
    }
    if (formData.budget <= 0) {
      toast.error('Please enter a valid budget');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/jobs`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Training requirement posted successfully! Showing matched trainers...');
      // Navigate to job detail page to show matched trainers
      navigate(`/job/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post job');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="post-job-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/corporate/dashboard')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <Link to="/" className="flex items-center gap-2" data-testid="logo">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-white" />
                </div>
                <span className="font-outfit font-bold text-xl text-slate-900">Training Solutions</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="font-outfit text-3xl font-bold text-slate-900 mb-2">
            Post Training Requirement
          </h1>
          <p className="text-slate-500">Find the perfect trainer for your corporate training needs</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
            <h2 className="font-outfit font-semibold text-lg text-slate-900 flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-blue-600" />
              Basic Information
            </h2>

            <div>
              <Label htmlFor="title">Training Requirement Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Leadership Development Workshop"
                className="mt-2"
                data-testid="job-title-input"
              />
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the training requirements, objectives, and target audience..."
                className="mt-2"
                rows={5}
                data-testid="job-description-input"
              />
            </div>

            {/* 3-Tier Training Type Selection */}
            <div>
              <Label>Training Category *</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                <select
                  value={mainCategory}
                  onChange={(e) => {
                    setMainCategory(e.target.value);
                    setSubCategory('');
                    setSubSubCategory('');
                    setFormData(prev => ({ ...prev, training_type: '' }));
                  }}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                >
                  <option value="">1. Main Category</option>
                  {getMainCategories().map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <select
                  value={subCategory}
                  onChange={(e) => {
                    setSubCategory(e.target.value);
                    setSubSubCategory('');
                    setFormData(prev => ({ ...prev, training_type: '' }));
                  }}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                  disabled={!mainCategory}
                >
                  <option value="">2. Sub Category</option>
                  {getSubCategories(mainCategory).map(sub => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
                <select
                  value={subSubCategory}
                  onChange={(e) => {
                    setSubSubCategory(e.target.value);
                    setFormData(prev => ({ ...prev, training_type: '' }));
                  }}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                  disabled={!subCategory}
                >
                  <option value="">3. Training Area</option>
                  {getSubSubCategories(mainCategory, subCategory).map(subSub => (
                    <option key={subSub} value={subSub}>{subSub}</option>
                  ))}
                </select>
                <select
                  value={formData.training_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, training_type: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm"
                  data-testid="training-type-select"
                  disabled={!subSubCategory}
                >
                  <option value="">4. Select Training</option>
                  {getTrainingTypes(mainCategory, subCategory, subSubCategory).map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Industry</Label>
                <select
                  value={formData.industry}
                  onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                  className="w-full h-10 px-3 mt-2 rounded-lg border border-slate-200"
                  data-testid="industry-select"
                >
                  <option value="">Select Industry</option>
                  {INDUSTRIES.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Country *</Label>
                <select
                  value={selectedCountry}
                  onChange={(e) => {
                    setSelectedCountry(e.target.value);
                    setFormData(prev => ({ ...prev, location: '' }));
                  }}
                  className="w-full h-10 px-3 mt-2 rounded-lg border border-slate-200"
                  data-testid="country-select"
                >
                  <option value="">Select Country</option>
                  {Object.entries(getCountriesByRegion()).map(([region, countries]) => (
                    <optgroup key={region} label={`── ${region} ──`}>
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div>
                <Label>City *</Label>
                <select
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full h-10 px-3 mt-2 rounded-lg border border-slate-200"
                  data-testid="location-select"
                  disabled={!selectedCountry}
                >
                  <option value="">{selectedCountry ? 'Select City' : 'Select Country First'}</option>
                  {getCitiesForCountry(selectedCountry).map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Venue & Contact Details */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
            <h2 className="font-outfit font-semibold text-lg text-slate-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              Venue & Contact Details
            </h2>

            <div>
              <Label htmlFor="venue_address">Venue Address *</Label>
              <Textarea
                id="venue_address"
                value={formData.venue_address}
                onChange={(e) => setFormData(prev => ({ ...prev, venue_address: e.target.value }))}
                placeholder="Full address where the training will take place"
                className="mt-2"
                rows={3}
                data-testid="venue-address-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_person">Contact Person Name *</Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                    placeholder="e.g., Rahul Sharma"
                    className="pl-10"
                    data-testid="contact-person-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="contact_phone">Contact Phone Number *</Label>
                <div className="relative mt-2">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                    placeholder="e.g., +91 98765 43210"
                    className="pl-10"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Skills */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
            <h2 className="font-outfit font-semibold text-lg text-slate-900">
              Required Skills *
            </h2>
            <div className="flex flex-wrap gap-2">
              {SKILLS.map(skill => (
                <button
                  key={skill}
                  type="button"
                  onClick={() => toggleSkill(skill)}
                  className={`px-4 py-2 text-sm rounded-full border transition-all ${
                    formData.skills_required.includes(skill)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                  data-testid={`skill-${skill.toLowerCase().replace(' ', '-')}`}
                >
                  {skill}
                </button>
              ))}
            </div>
            {formData.skills_required.length > 0 && (
              <p className="text-sm text-slate-500">
                Selected: {formData.skills_required.join(', ')}
              </p>
            )}
          </div>

          {/* Qualifications */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
            <h2 className="font-outfit font-semibold text-lg text-slate-900">
              Required Qualifications (Optional)
            </h2>
            <div className="flex gap-2">
              <Input
                value={newQualification}
                onChange={(e) => setNewQualification(e.target.value)}
                placeholder="e.g., MBA, PMP Certification"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addQualification())}
              />
              <Button type="button" onClick={addQualification} variant="outline">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {formData.qualifications_required.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {formData.qualifications_required.map((qual, i) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm flex items-center gap-2"
                  >
                    {qual}
                    <button type="button" onClick={() => removeQualification(qual)}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Schedule & Budget */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
            <h2 className="font-outfit font-semibold text-lg text-slate-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Schedule & Budget
            </h2>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Training Mode *</Label>
                <select
                  value={formData.training_mode}
                  onChange={(e) => setFormData(prev => ({ ...prev, training_mode: e.target.value }))}
                  className="w-full h-10 px-3 mt-2 rounded-lg border border-slate-200 text-sm"
                  data-testid="training-mode-select"
                >
                  <option value="on-site">On-Site</option>
                  <option value="online">Online</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>
              <div>
                <Label>Training Time</Label>
                <Input
                  value={formData.training_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, training_time: e.target.value }))}
                  placeholder="e.g., 9AM - 5PM"
                  className="mt-2"
                  data-testid="training-time-input"
                />
              </div>
              <div>
                <Label>Number of Trainers</Label>
                <Input
                  type="number"
                  value={formData.num_trainers}
                  onChange={(e) => setFormData(prev => ({ ...prev, num_trainers: parseInt(e.target.value) || 1 }))}
                  className="mt-2"
                  min={1}
                  data-testid="num-trainers-input"
                />
              </div>
            </div>

            {/* Trainer Roles */}
            <div>
              <Label>Trainer Roles (Optional)</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value)}
                  placeholder="e.g., Senior Trainer (Core Java)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (newRole.trim()) {
                        setFormData(prev => ({ ...prev, trainer_roles: [...prev.trainer_roles, newRole.trim()] }));
                        setNewRole('');
                      }
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={() => {
                  if (newRole.trim()) {
                    setFormData(prev => ({ ...prev, trainer_roles: [...prev.trainer_roles, newRole.trim()] }));
                    setNewRole('');
                  }
                }}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.trainer_roles.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.trainer_roles.map((role, i) => (
                    <span key={i} className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-full text-sm flex items-center gap-2">
                      {role}
                      <button type="button" onClick={() => setFormData(prev => ({ ...prev, trainer_roles: prev.trainer_roles.filter((_, idx) => idx !== i) }))}>
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="mt-2"
                  data-testid="start-date-input"
                />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="mt-2"
                  data-testid="end-date-input"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Duration (Hours) *</Label>
                <Input
                  type="number"
                  value={formData.duration_hours}
                  onChange={(e) => setFormData(prev => ({ ...prev, duration_hours: parseInt(e.target.value) || 0 }))}
                  className="mt-2"
                  min={1}
                  data-testid="duration-input"
                />
              </div>
              <div>
                <Label>Rate per Hour/Trainer</Label>
                <Input
                  type="number"
                  value={formData.rate_per_hour}
                  onChange={(e) => setFormData(prev => ({ ...prev, rate_per_hour: parseFloat(e.target.value) || 0 }))}
                  className="mt-2"
                  min={0}
                  placeholder="e.g., 800"
                  data-testid="rate-per-hour-input"
                />
              </div>
              <div>
                <Label>Total Budget *</Label>
                <div className="flex gap-2 mt-2">
                  <select
                    value={formData.budget_currency}
                    onChange={(e) => setFormData(prev => ({ ...prev, budget_currency: e.target.value }))}
                    className="h-10 px-2 rounded-lg border border-slate-200 text-sm w-24 shrink-0"
                    data-testid="budget-currency-select"
                  >
                    <option value="INR">₹ INR</option>
                    <option value="USD">$ USD</option>
                    <option value="EUR">€ EUR</option>
                    <option value="GBP">£ GBP</option>
                  </select>
                  <Input
                    type="number"
                    value={formData.budget}
                    onChange={(e) => setFormData(prev => ({ ...prev, budget: parseFloat(e.target.value) || 0 }))}
                    min={0}
                    placeholder="e.g., 28800"
                    data-testid="budget-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/corporate/dashboard')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="submit-job-btn"
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Posting...
                </div>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Post Requirement
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
